/**
 * Update Content feature module.
 * Handles document changes and annotation context updates.
 *
 * Responsibilities:
 * - Detect document changes via doc adapter
 * - Process each change: contentChanged, occurrenceChanged, targetTextNodeIdChanged
 * - Update annotation contexts in Velt SDK
 * - Update marks/nodes via marks adapter
 *
 * Architectural Rules:
 * - NO direct editor or Velt imports (uses adapters)
 * - Orchestrates: doc adapter → velt adapter → marks adapter
 * - All functions use ES6 arrow functions
 * - All imports use path aliases (@/)
 */
import { detectDocumentChanges } from '@/adapters/host/doc';
import { updateMarks } from '@/adapters/host/marks';
import { updateAnnotationContext } from '@/adapters/velt';
import { Constants } from '@/constants/common';
import { getAnnotation } from '@/core/state';
import { Console } from '@/utils/console';
/**
 * Handles content updates when document changes occur.
 * Detects annotation changes and updates Velt contexts accordingly.
 *
 * @param editorId Unique identifier for the editor
 * @param editor The editor instance
 * @param transaction The transaction object
 */
export const handleContentUpdate = (editorId, editor, transaction) => {
    var _a;
    const logPrefix = '[handleContentUpdate]';
    try {
        // Step 1: Detect document changes using host adapter
        // This requires a function to get annotation data from state
        const getAnnotationData = (annotationId) => {
            var _a, _b, _c, _d, _e, _f;
            const annotation = getAnnotation(editorId, annotationId);
            if (!annotation) {
                Console.warn(`${logPrefix} Annotation not found in state: ${annotationId}`);
                return null;
            }
            const originalText = ((_b = (_a = annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig) === null || _b === void 0 ? void 0 : _b.text) || '';
            const originalOccurrence = ((_d = (_c = annotation.context) === null || _c === void 0 ? void 0 : _c.textEditorConfig) === null || _d === void 0 ? void 0 : _d.occurrence) || 1;
            const targetTextNodeId = ((_f = (_e = annotation.context) === null || _e === void 0 ? void 0 : _e.textEditorConfig) === null || _f === void 0 ? void 0 : _f.targetTextNodeId) || '';
            return {
                annotation: annotation,
                originalText,
                originalOccurrence,
                targetTextNodeId,
            };
        };
        const changes = detectDocumentChanges(editor, transaction, Constants.MARK_NAME, getAnnotationData);
        if (changes.size === 0) {
            return;
        }
        // Step 2: Process each change (matching legacy updateVeltComments logic)
        for (const [annotationId, change] of Array.from(changes.entries())) {
            const changeLogPrefix = `${logPrefix}[${annotationId}]`;
            const annotation = getAnnotation(editorId, annotationId);
            if (!annotation) {
                Console.warn(`${changeLogPrefix} Annotation not found in state, skipping`);
                continue;
            }
            // Step 3: Build updated context (matching legacy updateVeltComments logic)
            if (!((_a = annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig)) {
                Console.warn(`${changeLogPrefix} No textEditorConfig, skipping`);
                continue;
            }
            const textEditorConfig = JSON.parse(JSON.stringify(annotation.context.textEditorConfig));
            if (change.contentChanged) {
                textEditorConfig.text = change.currentText;
            }
            if (change.occurrenceChanged) {
                textEditorConfig.occurrence = change.currentOccurrence;
            }
            if (change.targetTextNodeIdChanged) {
                textEditorConfig.targetTextNodeId = change.newTargetTextNodeId;
            }
            const updatedContext = Object.assign(Object.assign({}, annotation.context), { textEditorConfig });
            // Step 4: Update Velt context
            updateAnnotationContext(annotationId, updatedContext);
            // Step 5: Update state with new context
            // Note: State update should happen here, but for now we rely on renderComments
            // TODO: Add state update method if needed
        }
        // Step 6: Update marks if needed
        updateMarks(editor, changes);
    }
    catch (error) {
        Console.catch('Error handling content update:', error);
    }
};
